/**
 * M-X33 — DID management API client.
 *
 * Companion to apps/chaos-gateway/src/dids.ts.
 */

import { apiJson, ApiError } from './client';

export interface DidRow {
  id: string;
  orgId: string;
  provider: 'telnyx' | 'voximplant' | 'manual';
  number: string;
  providerId: string | null;
  monthlyCostUsd: number | null;
  purchasedAt: string | null;
  releasedAt: string | null;
  status: 'active' | 'released' | 'pending';
  cardId: string | null;
  roleId: string | null;
  defaultPrompt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface InboundRoute {
  id: string;
  didId: string;
  didNumber: string;
  cardId: string | null;
  roleId: string | null;
  defaultPrompt: string | null;
  ivrConfig: unknown | null;
  blocklistPatterns: string[];
  pattern: string;
  priority: number;
  active: boolean;
  blocklist: boolean;
  createdBy: string | null;
  createdAt: string;
}

export interface CreateDidBody {
  provider: 'telnyx' | 'voximplant' | 'manual';
  number?: string;
  areaCode?: string;
  country?: string;
  monthlyCost?: number;
  cardId?: string | null;
  roleId?: string | null;
  defaultPrompt?: string;
}

export const didsKeys = {
  list: (orgId: string) => ['dids', 'list', orgId] as const,
  routes: (didId: string) => ['dids', 'routes', didId] as const,
};

export const didsApi = {
  list: async (orgId: string): Promise<DidRow[]> => {
    const data = await apiJson<{ dids: DidRow[] }>(`/orgs/${orgId}/dids`);
    return data.dids;
  },

  create: async (orgId: string, body: CreateDidBody): Promise<DidRow> => {
    const data = await apiJson<{ did: DidRow }>(`/orgs/${orgId}/dids`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return data.did;
  },

  update: async (
    didId: string,
    patch: { cardId?: string | null; roleId?: string | null; defaultPrompt?: string | null },
  ): Promise<DidRow> => {
    const data = await apiJson<{ did: DidRow }>(`/dids/${didId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    return data.did;
  },

  release: async (didId: string): Promise<void> => {
    await apiJson<{ ok: boolean }>(`/dids/${didId}`, { method: 'DELETE' });
  },

  listRoutes: async (didId: string): Promise<InboundRoute[]> => {
    const data = await apiJson<{ routes: InboundRoute[] }>(`/dids/${didId}/routes`);
    return data.routes;
  },

  addRoute: async (
    didId: string,
    body: Partial<InboundRoute> & { pattern?: string; priority?: number },
  ): Promise<InboundRoute> => {
    const data = await apiJson<{ route: InboundRoute }>(`/dids/${didId}/routes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return data.route;
  },

  updateRoute: async (routeId: string, patch: Partial<InboundRoute>): Promise<InboundRoute> => {
    const data = await apiJson<{ route: InboundRoute }>(`/inbound-routes/${routeId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    return data.route;
  },

  deleteRoute: async (routeId: string): Promise<void> => {
    await apiJson<{ ok: boolean }>(`/inbound-routes/${routeId}`, { method: 'DELETE' });
  },
};

void ApiError; // used by callers
